import styled, { css } from 'styled-components';
import { themeColor, themeToggleColor } from '~/common/utils/theme';
import { Check, Copy } from '~/ui/assets/icons';
import { Tag, Typography } from '~/ui/components';

export const ProfileResumeContainer = styled.div`
  padding: 0 24px 0;
  display: flex;
  flex-direction: column;
  background-color: ${themeColor('background.primary', { dark: 'background.secondary' })};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding: 0 18px 0;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 18px;
    border-radius: 8px;
  }
`;

export const ProfileImageContainer = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  position: relative;

  .avatar {
    width: 88px;
    height: 88px;
    background: ${themeColor('element.placeholder', { dark: 'element.disabled' })};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    width: 112px;
    height: 112px;
    min-width: 112px;
    min-height: 112px;

    .avatar {
      width: 112px;
      height: 112px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    margin-top: 0;
    align-items: center;
  }
`;

type BagdeProps = {
  status: 'Aprovado' | 'Reprovado';
};

export const Bagde = styled.div<BagdeProps>`
  background-color: ${({ theme, status }) =>
    status === 'Aprovado' ? theme.colors.positive.primary : theme.colors.negative.primary};
  width: 24px;
  height: 24px;
  border-radius: 24px;
  position: absolute;
  right: 0;
  bottom: 0;
  text-align: center;

  svg {
    margin-top: 5px;
    color: ${themeColor('info.overPrimary')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    bottom: 10px;
    right: 11px;
  }
`;

export const ProfileInitials = styled(Typography).attrs(({ theme }) => ({
  type: 'paragraphSmall',
  element: 'div',
  color: themeToggleColor(theme, 'element.primary', { dark: 'element.secondary' }),
}))`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${themeColor('element.placeholder', { dark: 'element.disabled' })};
`;

export const ProfileName = styled(Typography).attrs(({ theme, withLink }) => ({
  type: 'paragraphSmall',
  weight: 700,
  element: 'span',
  color: themeToggleColor(theme, withLink ? 'brand.primary' : 'element.primary', {
    dark: 'element.secondary',
  }),
}))`
  text-decoration: ${({ withLink }) => (withLink ? 'underline' : 'none')};
`;

export const ProfileData = styled(Typography).attrs(({ theme }) => ({
  type: 'caption',
  element: 'span',
  color: themeToggleColor(theme, 'element.secondary', { dark: 'element.primary' }),
}))`
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ bold }) =>
    bold &&
    css`
      font-weight: 600;
      text-decoration: underline;
    `}

  svg {
    margin-right: 8px;
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  }
`;

export const ProfileDataLastAcess = styled(Typography).attrs(({ theme }) => ({
  type: 'caption',
  element: 'span',
  color: themeToggleColor(theme, 'element.secondary', { dark: 'element.primary' }),
}))`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;

  ${({ bold }) =>
    bold &&
    css`
      font-weight: 600;
      text-decoration: underline;
    `}

  svg {
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  }
`;

export const ProfileInitialInfoContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;
export const ProfileInitialInfoFooter = styled.div`
  display: flex;
  margin-top: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    display: none;
  }
`;
export const HistoryWrapper = styled.div`
  display: flex;
  padding-left: 1rem;
  border-left: 1px solid ${themeColor('neutral.secondaryAlt')};
  margin-left: 1rem;
`;

export const ProfileInfoWithPopover = styled(Typography).attrs(({ theme }) => ({
  type: 'caption',
  element: 'span',
  color: themeToggleColor(theme, 'element.secondary', { dark: 'element.primary' }),
}))`
  font-size: 13px;
  display: flex;
  gap: 6px;
  align-items: center;

  svg {
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  }

  span.text {
    margin-right: 4px;
  }
  .action-icon {
    cursor: pointer;
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  }
`;

export const ProfileInfo = styled.div`
  padding: 20px 0 22px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  justify-content: space-around;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    gap: 0px;
    padding: 8px 0 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    border: none;
    padding: 0px 0;
    width: 100%;
    gap: 0px;
  }

  .tag {
    width: fit-content;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const ProfileInfoSkeletonActions = styled.div`
  padding: 32px 0 22px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 220px;
  justify-content: space-between;
  align-items: flex-end;
`;

export const ProfileInfoSkeleton = styled.div`
  padding: 20px 0 22px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 220px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: 8px 0 0px;
    width: 220px;
    gap: 4px;
    margin-bottom: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    border: none;
    padding: 0px 0;
    width: 220px;
    gap: 4px;
    margin-bottom: 1rem;
  }

  .tag {
    width: fit-content;
  }
`;

type ProfileInfoItemProps = {
  selectable?: boolean;
  withoutMargin?: boolean;
};

export const ListWithArrow = styled.ul<ProfileInfoItemProps>`
  ${({ withoutMargin }) =>
    withoutMargin &&
    css`
      margin-bottom: 0px;
    `}

  li {
    list-style: none;
    display: flex;
    align-items: center;
    line-height: normal;
    color: ${themeColor('element.secondary')};
    cursor: ${({ selectable }) => (selectable ? 'pointer' : 'default')};

    &:hover {
      color: ${({ theme, selectable }) => selectable && theme.colors.brand.primary};
    }

    &.selected {
      color: ${({ theme, selectable }) => selectable && theme.colors.brand.primary};
      pointer-events: none;
    }

    &:not(:last-child) {
      margin-bottom: 10px;
    }

    svg {
      margin-right: 8px;
      color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
    }
  }
`;

export const BasicListRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
  border-top: 1px solid ${themeColor('divider.primary')};
  padding-top: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }

  .action-icon {
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
    position: relative;
    top: 2px;
  }
`;

export const BasicInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const InfoTitle = styled(Typography).attrs(({ theme }) => ({
  type: 'headingXSmall',
  element: 'span',
  color: themeToggleColor(theme, 'element.primary', { dark: 'element.secondary' }),
}))`
  font-size: 14px;
`;

export const InfoText = styled(Typography).attrs(({ theme }) => ({
  type: 'footnote',
  element: 'span',
  color: themeToggleColor(theme, 'element.secondary'),
}))``;

export const InfoDateText = styled(Typography).attrs(({ theme }) => ({
  type: 'footnote',
  element: 'span',
  color: themeToggleColor(theme, 'element.secondary'),
}))`
  font-weight: 600;
`;

export const AccountStatusTag = styled(Tag).attrs(({ theme }) => ({
  bgColor: themeToggleColor(theme, 'negative.secondary'),
  rounded: true,
  small: true,
}))`
  font-weight: 700;
  margin-top: 22px;
  font-size: 12px;
  width: fit-content;
  color: ${themeColor('negative.primaryAlt')};
`;

export const ExtraTagsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;

export const CopyButton = styled(Copy)`
  cursor: pointer;
  color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  width: 27px;
  height: 27px;

  :hover {
    color: ${themeColor('brand.primaryAlt')} !important;
  }
`;

export const Copied = styled(Check)`
  color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  width: 20px;
  height: 15px;
`;

export const Icon = styled.div`
  display: flex;
  height: 24px;
  min-width: 27px;
  align-items: center;
`;

export const NameContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
`;

interface ProfileWrapperProps {
  flexWrap?: boolean;
}

export const ProfileContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 1rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

export const ProfileContainerSkeleton = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

export const ProfileInfoWrapper = styled.div<ProfileWrapperProps>`
  display: flex;
  width: -webkit-fill-available;
  gap: 12px;
  flex-direction: row;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    flex-direction: column;
    gap: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    ${({ flexWrap }) =>
      flexWrap &&
      css`
        flex-wrap: wrap;
        gap: 8px 22px;
      `}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    ${({ flexWrap }) =>
      flexWrap &&
      css`
        flex-wrap: wrap;
        gap: 0;
      `}
  }

  span {
    width: fit-content;
  }
`;

export const ProfileWrapper = styled.div<ProfileWrapperProps>`
  display: flex;
  width: -webkit-fill-available;
  gap: 16px;
  flex-direction: row;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    ${({ flexWrap }) =>
      flexWrap &&
      css`
        flex-wrap: wrap;
        gap: 8px 22px;
      `}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    margin-top: 1rem;
    margin-bottom: 1rem;
    gap: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  span {
    width: fit-content;
  }
`;

export const TagsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-left: 1rem;
  border-left: 1px solid ${themeColor('neutral.secondaryAlt')};
  margin-left: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
  }
`;

export const IconsWrapper = styled.div`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    align-items: center;

    svg {
      color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1rem;

    svg {
      color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
    }
  }
`;

export const TooltipBody = styled.div`
  display: flex;
`;

export const TagContainer = styled.div`
  display: flex;
`;

export const ClockTooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .date-formated {
    font-weight: 700;
  }
`;
